import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useTheme } from '@mui/material/styles';
import deficitDetailsService, {
  BulkDeficitIncreaseRow,
  BulkDeficitIncreaseResponse,
  BulkDeficitUpdateRow,
  DeficitDetail,
  DeficitTransaction,
  LatestRentUpdateDates,
} from '../services/deficitDetailsService';
import vehicleService from '../services/vehicleService';
import { hasAnyRole } from '../utils/roleUtils';

const canExport = hasAnyRole([
  'Super Admin',
  'MIS_Management'
]);

const DeficitDetails: React.FC = () => {
  const theme = useTheme();
  type RentWeekState =
    | { status: 'loading' }
    | { status: 'loaded'; dates: LatestRentUpdateDates }
    | { status: 'not-found' }
    | { status: 'error' };
  
  // State management
  const [data, setData] = useState<DeficitDetail[]>([]);
  const [rentWeekByRider, setRentWeekByRider] = useState<
    Record<string, RentWeekState>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchPublicRiderId, setSearchPublicRiderId] = useState('');
  const [tempSearchPublicRiderId, setTempSearchPublicRiderId] = useState('');
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updateSubmitting, setUpdateSubmitting] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [increaseDialogOpen, setIncreaseDialogOpen] = useState(false);
  const [increaseSubmitting, setIncreaseSubmitting] = useState(false);
  const [increaseError, setIncreaseError] = useState<string | null>(null);
  const [increaseBulkDialogOpen, setIncreaseBulkDialogOpen] = useState(false);
  const [increaseBulkLoading, setIncreaseBulkLoading] = useState(false);
  const [increaseBulkFile, setIncreaseBulkFile] = useState<File | null>(null);
  const [increaseBulkRows, setIncreaseBulkRows] = useState<BulkDeficitIncreaseRow[]>([]);
  const [increaseBulkError, setIncreaseBulkError] = useState<string | null>(null);
  const [increaseBulkResult, setIncreaseBulkResult] = useState<BulkDeficitIncreaseResponse | null>(null);
  const [increaseForm, setIncreaseForm] = useState({
    publicRiderId: '',
    totalRentAmount: '',
    channelAmount: '',
    damageCost: '',
    weekStartDate: '',
    weekEndDate: '',
  });
  const [updateForm, setUpdateForm] = useState({
    publicRiderId: '',
    totalRentAmount: '',
    channelAmount: '',
    damageCost: '',
  });
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkRows, setBulkRows] = useState<BulkDeficitUpdateRow[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkResult, setBulkResult] = useState<{ summary: { totalRows: number; successCount: number; failureCount: number }; failures: Array<{ rowNumber: number; publicRiderId: string; message: string }> } | null>(null);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [transactionError, setTransactionError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<DeficitTransaction[]>([]);
  const [transactionPage, setTransactionPage] = useState(0);
  const [transactionTotal, setTransactionTotal] = useState(0);
  const [transactionSearch, setTransactionSearch] = useState('');
  const [transactionSearchInput, setTransactionSearchInput] = useState('');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMode, setExportMode] = useState<'all' | 'city' | 'date'>('all');
  const [exportCity, setExportCity] = useState('');
  const [cityOptions, setCityOptions] = useState<string[]>([]);
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');

  const exportFilterValid = exportMode === 'all'
    || (exportMode === 'city' && Boolean(exportCity))
    || (exportMode === 'date' && Boolean(exportFromDate || exportToDate));

  useEffect(() => {
    const loadCityOptions = async () => {
      const response = await vehicleService.getCities({ isActive: true, isOperational: true });
      const serviceCities = (response?.data || [])
        .map((city: any) => city.name)
        .filter((name: any): name is string => Boolean(name));
      const loadedCities = data
        .map((row) => row.rider?.city)
        .filter((name): name is string => Boolean(name));
      setCityOptions(Array.from(new Set([...serviceCities, ...loadedCities])).sort());
    };

    void loadCityOptions();
  }, [data]);

  // Fetch deficit details
  const fetchDeficitDetails = async (searchPage = 0, searchTerm = '') => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        page: searchPage + 1, // API uses 1-based pagination
        limit: rowsPerPage,
      };
      
      if (searchTerm.trim()) {
        params.publicRiderId = searchTerm.trim();
      }

      const response = await deficitDetailsService.getDeficitDetails(params);
      
      if (response.success) {
        const rows = Array.isArray(response.data) ? response.data : response.data ? [response.data] : [];
        setData(rows);
        setTotal(response.meta?.total ?? rows.length);
        setTotalPages(response.meta?.totalPages ?? (rows.length > 0 ? 1 : 0));
      } else {
        setError('Failed to fetch deficit details');
      }
    } catch (err: any) {
      console.error('Error fetching deficit details:', err);
      setError(err.response?.data?.message || err.message || 'An error occurred while fetching deficit details');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load and when pagination/search changes
  useEffect(() => {
    fetchDeficitDetails(page, searchPublicRiderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchPublicRiderId]);

  useEffect(() => {
    const publicRiderIds = [...new Set(data.map((row) => row.publicRiderId))];
    if (!publicRiderIds.length) {
      setRentWeekByRider({});
      return;
    }

    let isCurrent = true;
    setRentWeekByRider(
      Object.fromEntries(publicRiderIds.map((id) => [id, { status: 'loading' }])),
    );

    const loadLatestRentWeeks = async () => {
      const results = await Promise.all(
        publicRiderIds.map(async (publicRiderId) => {
          try {
            const dates = await deficitDetailsService.getLatestRentUpdateDates(publicRiderId);
            return { publicRiderId, state: { status: 'loaded', dates } as RentWeekState };
          } catch (requestError) {
            const status =
              typeof requestError === 'object' &&
              requestError !== null &&
              'response' in requestError &&
              typeof requestError.response === 'object' &&
              requestError.response !== null &&
              'status' in requestError.response
                ? requestError.response.status
                : undefined;

            if (status === 404) {
              return { publicRiderId, state: { status: 'not-found' } as RentWeekState };
            }

            console.error(
              `Unable to load latest rent update dates for rider ${publicRiderId}:`,
              requestError,
            );
            return { publicRiderId, state: { status: 'error' } as RentWeekState };
          }
        }),
      );

      if (isCurrent) {
        setRentWeekByRider(
          Object.fromEntries(results.map(({ publicRiderId, state }) => [publicRiderId, state])),
        );
      }
    };

    void loadLatestRentWeeks();
    return () => {
      isCurrent = false;
    };
  }, [data]);

  // Handle pagination change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearch = () => {
    setPage(0);
    setSearchPublicRiderId(tempSearchPublicRiderId);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setTempSearchPublicRiderId('');
    setSearchPublicRiderId('');
    setPage(0);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchDeficitDetails(page, searchPublicRiderId);
  };

  // Handle key press on search
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const updateTotal = ['totalRentAmount', 'channelAmount', 'damageCost'].reduce(
    (total, field) => total + (Number(updateForm[field as keyof typeof updateForm]) || 0),
    0,
  );

  const openUpdateDialog = () => {
    setUpdateForm({
      publicRiderId: searchPublicRiderId,
      totalRentAmount: '',
      channelAmount: '',
      damageCost: '',
    });
    setUpdateError(null);
    setUpdateDialogOpen(true);
  };

  const closeUpdateDialog = () => {
    if (!updateSubmitting) setUpdateDialogOpen(false);
  };

  const handleUpdateSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (updateTotal <= 0) {
      setUpdateError('Enter an amount greater than zero in at least one deficit field.');
      return;
    }

    try {
      setUpdateSubmitting(true);
      setUpdateError(null);
      setError(null);
      await deficitDetailsService.updateExistingDeficitDetails({
        publicRiderId: updateForm.publicRiderId.trim(),
        totalRentAmount: Number(updateForm.totalRentAmount),
        channelAmount: Number(updateForm.channelAmount),
        damageCost: Number(updateForm.damageCost),
      });
      setUpdateDialogOpen(false);
      await fetchDeficitDetails(page, searchPublicRiderId);
    } catch (err: any) {
      setUpdateError(err.response?.data?.message || err.message || 'Failed to update deficit details');
    } finally {
      setUpdateSubmitting(false);
    }
  };

  const openIncreaseDialog = () => {
    setIncreaseForm({
      publicRiderId: searchPublicRiderId,
      totalRentAmount: '',
      channelAmount: '',
      damageCost: '',
      weekStartDate: '',
      weekEndDate: '',
    });
    setIncreaseError(null);
    setIncreaseDialogOpen(true);
  };

  const handleIncreaseSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const amounts = {
      totalRentAmount: Number(increaseForm.totalRentAmount || 0),
      channelAmount: Number(increaseForm.channelAmount || 0),
      damageCost: Number(increaseForm.damageCost || 0),
    };
    if (Object.values(amounts).some((amount) => !Number.isFinite(amount) || amount < 0)) {
      setIncreaseError('Enter valid non-negative deficit amounts.');
      return;
    }
    if (Object.values(amounts).every((amount) => amount === 0)) {
      setIncreaseError('Enter an amount greater than zero in at least one deficit field.');
      return;
    }
    if (amounts.totalRentAmount > 0 && (!increaseForm.weekStartDate || !increaseForm.weekEndDate)) {
      setIncreaseError('Enter both week start and week end dates when increasing rent deficit.');
      return;
    }
    if (Boolean(increaseForm.weekStartDate) !== Boolean(increaseForm.weekEndDate)) {
      setIncreaseError('Enter both week start and week end dates.');
      return;
    }
    if (increaseForm.weekStartDate && increaseForm.weekEndDate && increaseForm.weekStartDate > increaseForm.weekEndDate) {
      setIncreaseError('Week start date must be on or before week end date.');
      return;
    }

    const publicRiderId = increaseForm.publicRiderId.trim();
    try {
      setIncreaseSubmitting(true);
      setIncreaseError(null);
      await deficitDetailsService.increaseDeficitDetails({
        publicRiderId,
        ...amounts,
        weekStartDate: increaseForm.weekStartDate || null,
        weekEndDate: increaseForm.weekEndDate || null,
      });
      setIncreaseDialogOpen(false);
      setTempSearchPublicRiderId(publicRiderId);
      setSearchPublicRiderId(publicRiderId);
      setPage(0);
      await fetchDeficitDetails(0, publicRiderId);
    } catch (err: any) {
      setIncreaseError(err.response?.data?.message || err.message || 'Failed to increase deficit details');
    } finally {
      setIncreaseSubmitting(false);
    }
  };

  const openIncreaseBulkDialog = () => {
    setIncreaseBulkFile(null);
    setIncreaseBulkRows([]);
    setIncreaseBulkError(null);
    setIncreaseBulkResult(null);
    setIncreaseBulkDialogOpen(true);
  };

  const downloadIncreaseBulkTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet([
      {
        publicRiderId: 'DEL-26-R000102',
        totalRentAmount: 600,
        channelAmount: 400,
        damageCost: 222,
        weekStartDate: '2026-10-05',
        weekEndDate: '2026-10-11',
      },
    ], {
      header: ['publicRiderId', 'totalRentAmount', 'channelAmount', 'damageCost', 'weekStartDate', 'weekEndDate'],
    });
    worksheet['!cols'] = [
      { wch: 24 },
      { wch: 20 },
      { wch: 18 },
      { wch: 16 },
      { wch: 18 },
      { wch: 18 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Deficit Increase');
    const file = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([file], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      'deficit-increase-template.xlsx',
    );
  };

  const handleIncreaseBulkFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      setIncreaseBulkFile(file);
      setIncreaseBulkRows([]);
      setIncreaseBulkError(null);
      setIncreaseBulkResult(null);
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) throw new Error('The selected workbook has no worksheet.');
      const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[sheetName], { defval: '' });
      if (rawRows.length === 0) throw new Error('The selected file is empty.');
      if (rawRows.length > 500) throw new Error('A maximum of 500 rows can be uploaded at a time.');

      const requiredColumns = [
        'publicRiderId',
        'totalRentAmount',
        'channelAmount',
        'damageCost',
        'weekStartDate',
        'weekEndDate',
      ];
      const columns = Object.keys(rawRows[0]);
      const missingColumns = requiredColumns.filter((column) => !columns.includes(column));
      if (missingColumns.length > 0) throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);

      const parsedRows = rawRows.map((row, index): BulkDeficitIncreaseRow => {
        const publicRiderId = String(row.publicRiderId ?? '').trim();
        const amounts = {
          totalRentAmount: Number(row.totalRentAmount ?? 0),
          channelAmount: Number(row.channelAmount ?? 0),
          damageCost: Number(row.damageCost ?? 0),
        };
        const parseDate = (value: unknown, field: string): string | null => {
          if (value === undefined || value === null || value === '') return null;
          let year: number;
          let month: number;
          let day: number;
          if (typeof value === 'number') {
            const parsed = XLSX.SSF.parse_date_code(value);
            if (!parsed) throw new Error(`Row ${index + 2}: ${field} must be a valid date.`);
            ({ y: year, m: month, d: day } = parsed);
          } else if (value instanceof Date && !Number.isNaN(value.getTime())) {
            year = value.getUTCFullYear();
            month = value.getUTCMonth() + 1;
            day = value.getUTCDate();
          } else if (typeof value === 'string') {
            const text = value.trim();
            const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text)
              || /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(text);
            if (!match) throw new Error(`Row ${index + 2}: ${field} must use YYYY-MM-DD or DD/MM/YYYY.`);
            if (match[1].length === 4) {
              year = Number(match[1]);
              month = Number(match[2]);
              day = Number(match[3]);
            } else {
              day = Number(match[1]);
              month = Number(match[2]);
              year = Number(match[3]);
            }
          } else {
            throw new Error(`Row ${index + 2}: ${field} must be a valid date.`);
          }
          const date = new Date(Date.UTC(year, month - 1, day));
          if (
            !Number.isFinite(date.getTime())
            || date.getUTCFullYear() !== year
            || date.getUTCMonth() + 1 !== month
            || date.getUTCDate() !== day
          ) {
            throw new Error(`Row ${index + 2}: ${field} must be a valid date.`);
          }
          return date.toISOString().slice(0, 10);
        };
        const weekStartDate = parseDate(row.weekStartDate, 'weekStartDate');
        const weekEndDate = parseDate(row.weekEndDate, 'weekEndDate');
        if (!publicRiderId) throw new Error(`Row ${index + 2}: publicRiderId is required.`);
        if (Object.values(amounts).some((amount) => !Number.isFinite(amount) || amount < 0)) {
          throw new Error(`Row ${index + 2}: amounts must be valid non-negative numbers.`);
        }
        if (Object.values(amounts).every((amount) => amount === 0)) {
          throw new Error(`Row ${index + 2}: at least one amount must be greater than zero.`);
        }
        if (Boolean(weekStartDate) !== Boolean(weekEndDate)) {
          throw new Error(`Row ${index + 2}: both week dates must be provided together.`);
        }
        if (amounts.totalRentAmount > 0 && (!weekStartDate || !weekEndDate)) {
          throw new Error(`Row ${index + 2}: week dates are required when increasing rent deficit.`);
        }
        if (weekStartDate && weekEndDate && weekStartDate > weekEndDate) {
          throw new Error(`Row ${index + 2}: weekStartDate must be on or before weekEndDate.`);
        }
        return { publicRiderId, ...amounts, weekStartDate, weekEndDate };
      });
      setIncreaseBulkRows(parsedRows);
    } catch (error: any) {
      setIncreaseBulkFile(null);
      setIncreaseBulkRows([]);
      setIncreaseBulkError(error?.message || 'Unable to read the selected file.');
    }
  };

  const handleIncreaseBulkSubmit = async () => {
    if (!increaseBulkFile || increaseBulkRows.length === 0) {
      setIncreaseBulkError('Select a valid file before uploading.');
      return;
    }
    try {
      setIncreaseBulkLoading(true);
      setIncreaseBulkError(null);
      const response = await deficitDetailsService.bulkIncreaseDeficitDetails(increaseBulkRows);
      setIncreaseBulkResult(response);
      if (response.summary.failureCount === 0 && response.summary.warningCount === 0) {
        setIncreaseBulkDialogOpen(false);
      }
      await fetchDeficitDetails(page, searchPublicRiderId);
    } catch (error: any) {
      const response = error.response?.data;
      if (response?.summary) {
        setIncreaseBulkResult(response as BulkDeficitIncreaseResponse);
        await fetchDeficitDetails(page, searchPublicRiderId);
      } else {
        setIncreaseBulkError(response?.message || error.message || 'Bulk deficit increase failed.');
      }
    } finally {
      setIncreaseBulkLoading(false);
    }
  };

  const openBulkDialog = () => {
    setBulkFile(null);
    setBulkRows([]);
    setBulkError(null);
    setBulkResult(null);
    setBulkDialogOpen(true);
  };

  const downloadBulkTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet([
      {
        publicRiderId: 'DEL-26-R000102',
        totalRentAmount: 600,
        channelAmount: 400,
        damageCost: 222,
      },
    ], {
      header: ['publicRiderId', 'totalRentAmount', 'channelAmount', 'damageCost'],
    });
    worksheet['!cols'] = [
      { wch: 24 },
      { wch: 20 },
      { wch: 18 },
      { wch: 16 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Deficit Updates');
    const file = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([file], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'deficit-update-template.xlsx');
  };

  const handleBulkFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      setBulkFile(file);
      setBulkError(null);
      setBulkResult(null);
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
      if (rawRows.length === 0) throw new Error('The selected file is empty.');

      const requiredColumns = [
        'publicRiderId',
        'totalRentAmount',
        'channelAmount',
        'damageCost',
      ];
      const columns = Object.keys(rawRows[0]);
      const missingColumns = requiredColumns.filter((column) => !columns.includes(column));
      if (missingColumns.length > 0) throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
      if (rawRows.length > 500) throw new Error('A maximum of 500 rows can be uploaded at a time.');

      const parsedRows = rawRows.map((row, index) => {
        const publicRiderId = String(row.publicRiderId ?? '').trim();
        const amounts = {
          totalRentAmount: Number(row.totalRentAmount ?? 0),
          channelAmount: Number(row.channelAmount ?? 0),
          damageCost: Number(row.damageCost ?? 0),
        };
        if (!publicRiderId) throw new Error(`Row ${index + 2}: publicRiderId is required.`);
        if (Object.values(amounts).some((amount) => !Number.isFinite(amount) || amount < 0)) {
          throw new Error(`Row ${index + 2}: amounts must be valid non-negative numbers.`);
        }
        if (Object.values(amounts).every((amount) => amount === 0)) {
          throw new Error(`Row ${index + 2}: at least one amount must be greater than zero.`);
        }
        return { publicRiderId, ...amounts };
      });
      setBulkRows(parsedRows);
    } catch (error: any) {
      setBulkFile(null);
      setBulkRows([]);
      setBulkError(error?.message || 'Unable to read the selected file.');
    }
  };

  const handleBulkSubmit = async () => {
    if (!bulkFile || bulkRows.length === 0) {
      setBulkError('Select a valid file before uploading.');
      return;
    }
    try {
      setBulkLoading(true);
      setBulkError(null);
      const response = await deficitDetailsService.bulkUpdateDeficitDetails(bulkRows);
      setBulkResult({ summary: response.summary, failures: response.failures });
      if (response.summary.failureCount === 0) {
        setBulkDialogOpen(false);
      }
      await fetchDeficitDetails(page, searchPublicRiderId);
    } catch (error: any) {
      const response = error.response?.data;
      if (response?.summary) {
        setBulkResult({ summary: response.summary, failures: response.failures || [] });
      } else {
        setBulkError(response?.message || error.message || 'Bulk update failed.');
      }
    } finally {
      setBulkLoading(false);
    }
  };

  const fetchDeficitTransactions = async (requestedPage = 0, searchTerm = '') => {
    try {
      setTransactionLoading(true);
      setTransactionError(null);
      const response = await deficitDetailsService.getDeficitTransactions({
        page: requestedPage + 1,
        limit: 20,
        ...(searchTerm.trim() ? { publicRiderId: searchTerm.trim() } : {}),
      });
      setTransactions(response.data);
      setTransactionTotal(response.meta.total);
    } catch (err: any) {
      console.error('Error fetching deficit transactions:', err);
      setTransactionError(err.response?.data?.message || err.message || 'Failed to fetch deficit transactions');
      setTransactions([]);
      setTransactionTotal(0);
    } finally {
      setTransactionLoading(false);
    }
  };

  const openTransactionDialog = () => {
    setTransactionDialogOpen(true);
    setTransactionPage(0);
    setTransactionSearch('');
    setTransactionSearchInput('');
    void fetchDeficitTransactions(0, '');
  };

  const handleTransactionSearch = () => {
    setTransactionPage(0);
    setTransactionSearch(transactionSearchInput);
    void fetchDeficitTransactions(0, transactionSearchInput);
  };

  const handleTransactionPageChange = (_event: unknown, newPage: number) => {
    setTransactionPage(newPage);
    void fetchDeficitTransactions(newPage, transactionSearch);
  };

  const exportDeficitDetails = async () => {
    setExporting(true);
    try {
      const params = exportMode === 'city'
        ? { city: exportCity.trim() }
        : exportMode === 'date'
        ? { fromDate: exportFromDate, toDate: exportToDate }
        : undefined;
      const response = await deficitDetailsService.exportDeficitDetails(params);
      const rawContentType = response.headers?.['content-type'] || response.headers?.['Content-Type'];
      const contentType = typeof rawContentType === 'string'
        ? rawContentType
        : Array.isArray(rawContentType)
        ? rawContentType[0]
        : '';

      if (contentType.includes('application/json') || contentType.includes('text/')) {
        throw new Error('Export response was not a valid Excel file');
      }

      const blob = new Blob([response.data], {
        type: contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const contentDisposition = response.headers?.['content-disposition'] || response.headers?.['Content-Disposition'] || '';
      const filenameMatch = /filename\*?=(?:UTF-8'')?"?([^";\n]+)"?/i.exec(contentDisposition);
      const filename = filenameMatch?.[1] || `deficit_details_${new Date().toISOString().split('T')[0]}.xlsx`;
      saveAs(blob, filename);
      setExportDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to export deficit details');
    } finally {
      setExporting(false);
    }
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRentWeekDate = (dateString: string | null) => {
    if (!dateString) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString.slice(0, 10));
    return match ? `${match[3]}/${match[2]}/${match[1]}` : null;
  };

  const getRentWeekLabel = (publicRiderId: string) => {
    const rentWeek = rentWeekByRider[publicRiderId];
    if (!rentWeek || rentWeek.status === 'loading') return 'Loading week dates…';
    if (rentWeek.status === 'not-found') return 'No week dates found';
    if (rentWeek.status === 'error') return 'Unable to load week dates';

    const startDate = formatRentWeekDate(rentWeek.dates.weekStartDate);
    const endDate = formatRentWeekDate(rentWeek.dates.weekEndDate);
    return startDate && endDate ? `${startDate} – ${endDate}` : 'Week dates unavailable';
  };

  // Calculate total pending for a row
  const calculatePending = (row: DeficitDetail) => {
    const rentPending = row.totalRentAmount - row.totalRentPaid;
    const channelPending = row.channelAmount - row.totalChannelPaid;
    const damagePending = row.damageCost - row.totalDamagePaid;
    return rentPending + channelPending + damagePending;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header and actions */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Deficit Details
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            View and manage rider deficit details with payment tracking
          </Typography>
        </Box>
        <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={4}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', lineHeight: 1.5, mb: 0.75, fontWeight: 600 }}
              >
                Deficit management
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={openIncreaseDialog}
                  sx={{ textTransform: 'none' }}
                  disabled={!canExport}
                >
                  Increase / create
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EditNoteIcon />}
                  onClick={openUpdateDialog}
                  sx={{ textTransform: 'none' }}
                  disabled={!canExport}
                >
                  Update deficit
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', lineHeight: 1.5, mb: 0.75, fontWeight: 600 }}
              >
                Bulk operations
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={openIncreaseBulkDialog}
                  sx={{ textTransform: 'none' }}
                  disabled={!canExport}
                >
                  Bulk increase
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={openBulkDialog}
                  sx={{ textTransform: 'none' }}
                  disabled={!canExport}
                >
                  Bulk update
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={12} lg={4}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', lineHeight: 1.5, mb: 0.75, fontWeight: 600 }}
              >
                Reports
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={openTransactionDialog}
                  sx={{ textTransform: 'none' }}
                  disabled={!canExport}
                >
                  View transactions
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => setExportDialogOpen(true)}
                  sx={{ textTransform: 'none' }}
                  disabled={exporting}
                >
                  Download Excel
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Search and Filter Section */}
      <Card sx={{ mb: 3, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search by Public Rider ID"
                value={tempSearchPublicRiderId}
                onChange={(e) => setTempSearchPublicRiderId(e.target.value)}
                onKeyPress={handleKeyPress}
                size="small"
                variant="outlined"
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearSearch}
                  startIcon={<ClearIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Clear
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleRefresh}
                  startIcon={<RefreshIcon />}
                  sx={{ textTransform: 'none' }}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error} 
        </Alert>
      )}

      <Dialog open={updateDialogOpen} onClose={closeUpdateDialog} maxWidth="sm" fullWidth>
        <Box component="form" onSubmit={handleUpdateSubmit}>
          <DialogTitle sx={{ pb: 1 }}>Update deficit details</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the amounts to deduct from this rider's existing deficit.
            </Typography>
            {updateError && <Alert severity="error" sx={{ mb: 2 }}>{updateError}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Public Rider ID"
                  placeholder="e.g. DEL-26-R000098"
                  value={updateForm.publicRiderId}
                  onChange={(event) => setUpdateForm({ ...updateForm, publicRiderId: event.target.value })}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
              {[
                ['totalRentAmount', 'Total rent amount'],
                ['channelAmount', 'Challen amount'],
                ['damageCost', 'Damage cost'],
              ].map(([field, label]) => (
                <Grid item xs={12} sm={4} key={field}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label={label}
                    value={updateForm[field as keyof typeof updateForm]}
                    onChange={(event) => setUpdateForm({ ...updateForm, [field]: event.target.value })}
                    inputProps={{ min: 0, step: '0.01' }}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>₹</Typography> }}
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: theme.palette.action.hover }}>
                  <Typography variant="subtitle2" color="text.secondary">Total amount</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {formatCurrency(updateTotal)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeUpdateDialog} disabled={updateSubmitting} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={updateSubmitting} startIcon={updateSubmitting ? <CircularProgress size={18} /> : <EditNoteIcon />} sx={{ textTransform: 'none' }}>
              {updateSubmitting ? 'Saving...' : 'Save deficit details'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={increaseDialogOpen} onClose={() => !increaseSubmitting && setIncreaseDialogOpen(false)} maxWidth="sm" fullWidth>
        <Box component="form" onSubmit={handleIncreaseSubmit}>
          <DialogTitle sx={{ pb: 1 }}>Increase or create deficit</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These amounts will be added to the rider's existing deficit. A deficit record will be created if one does not exist.
            </Typography>
            {increaseError && <Alert severity="error" sx={{ mb: 2 }}>{increaseError}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Public Rider ID"
                  placeholder="e.g. DEL-26-R000098"
                  value={increaseForm.publicRiderId}
                  onChange={(event) => setIncreaseForm({ ...increaseForm, publicRiderId: event.target.value })}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
              {[
                ['totalRentAmount', 'Total rent amount'],
                ['channelAmount', 'Channel amount'],
                ['damageCost', 'Damage cost'],
              ].map(([field, label]) => (
                <Grid item xs={12} sm={4} key={field}>
                  <TextField
                    fullWidth
                    type="number"
                    label={label}
                    value={increaseForm[field as keyof typeof increaseForm]}
                    onChange={(event) => setIncreaseForm({ ...increaseForm, [field]: event.target.value })}
                    inputProps={{ min: 0, step: '0.01' }}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>₹</Typography> }}
                  />
                </Grid>
              ))}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required={Number(increaseForm.totalRentAmount) > 0}
                  type="date"
                  label="Week start date"
                  value={increaseForm.weekStartDate}
                  onChange={(event) => setIncreaseForm({ ...increaseForm, weekStartDate: event.target.value })}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: increaseForm.weekEndDate || undefined }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required={Number(increaseForm.totalRentAmount) > 0}
                  type="date"
                  label="Week end date"
                  value={increaseForm.weekEndDate}
                  onChange={(event) => setIncreaseForm({ ...increaseForm, weekEndDate: event.target.value })}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: increaseForm.weekStartDate || undefined }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setIncreaseDialogOpen(false)} disabled={increaseSubmitting} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button type="submit" variant="contained" color="success" disabled={increaseSubmitting} startIcon={increaseSubmitting ? <CircularProgress size={18} /> : <AddCircleOutlineIcon />} sx={{ textTransform: 'none' }}>
              {increaseSubmitting ? 'Saving...' : 'Increase deficit'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={increaseBulkDialogOpen}
        onClose={() => !increaseBulkLoading && setIncreaseBulkDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Bulk increase or create deficit</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload an Excel or CSV file with columns: publicRiderId, totalRentAmount, channelAmount, damageCost,
            weekStartDate, weekEndDate. Both week dates are required when totalRentAmount is greater than zero.
            Dates may use YYYY-MM-DD or DD/MM/YYYY. Up to 500 riders can be processed per upload.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={increaseBulkLoading}>
              {increaseBulkFile ? 'Choose another file' : 'Choose file'}
              <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={handleIncreaseBulkFileChange} />
            </Button>
            <Button variant="text" startIcon={<DownloadIcon />} onClick={downloadIncreaseBulkTemplate} disabled={increaseBulkLoading}>
              Download template
            </Button>
          </Stack>
          {increaseBulkFile && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              {increaseBulkFile.name} {increaseBulkRows.length > 0 ? `(${increaseBulkRows.length} rows ready)` : ''}
            </Typography>
          )}
          {increaseBulkError && <Alert severity="error" sx={{ mb: 2 }}>{increaseBulkError}</Alert>}
          {increaseBulkResult && (
            <Box sx={{ mt: 2 }}>
              <Alert severity={increaseBulkResult.summary.failureCount ? 'warning' : increaseBulkResult.summary.warningCount ? 'info' : 'success'}>
                {increaseBulkResult.summary.successCount} of {increaseBulkResult.summary.totalRows} rows increased successfully.
                {increaseBulkResult.summary.failureCount > 0 && ` ${increaseBulkResult.summary.failureCount} rows failed.`}
                {increaseBulkResult.summary.warningCount > 0 && ` ${increaseBulkResult.summary.warningCount} rows have warnings.`}
              </Alert>
              {[...increaseBulkResult.failures, ...increaseBulkResult.warnings].length > 0 && (
                <Box sx={{ mt: 1, maxHeight: 220, overflowY: 'auto' }}>
                  {increaseBulkResult.failures.map((failure) => (
                    <Typography color="error" variant="body2" key={`failure-${failure.rowNumber}-${failure.publicRiderId}`}>
                      Row {failure.rowNumber} ({failure.publicRiderId || 'no rider ID'}): {failure.message}
                    </Typography>
                  ))}
                  {increaseBulkResult.warnings.map((warning) => (
                    <Typography color="text.secondary" variant="body2" key={`warning-${warning.rowNumber}-${warning.publicRiderId}`}>
                      Row {warning.rowNumber} ({warning.publicRiderId}): {warning.message}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIncreaseBulkDialogOpen(false)} disabled={increaseBulkLoading}>Close</Button>
          <Button
            variant="contained"
            onClick={handleIncreaseBulkSubmit}
            disabled={increaseBulkLoading || increaseBulkRows.length === 0}
            startIcon={increaseBulkLoading ? <CircularProgress size={18} /> : <UploadFileIcon />}
          >
            {increaseBulkLoading ? 'Processing...' : 'Upload and increase'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDialogOpen} onClose={() => !bulkLoading && setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Update Deficit Details</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload an Excel or CSV file with columns: publicRiderId, totalRentAmount, channelAmount, damageCost.
          </Typography>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={bulkLoading}>
            {bulkFile ? 'Choose another file' : 'Choose file'}
            <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={handleBulkFileChange} />
          </Button>
          <Button variant="text" startIcon={<DownloadIcon />} onClick={downloadBulkTemplate} disabled={bulkLoading}>
            Download template
          </Button>
          {bulkFile && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {bulkFile.name} {bulkRows.length > 0 ? `(${bulkRows.length} rows ready)` : ''}
            </Typography>
          )}
          {bulkError && <Alert severity="error" sx={{ mt: 2 }}>{bulkError}</Alert>}
          {bulkResult && (
            <Box sx={{ mt: 2 }}>
              <Alert severity={bulkResult.summary.failureCount ? 'warning' : 'success'}>
                {bulkResult.summary.successCount} of {bulkResult.summary.totalRows} rows updated successfully.
              </Alert>
              {bulkResult.failures.length > 0 && (
                <Box sx={{ mt: 1, maxHeight: 180, overflowY: 'auto' }}>
                  {bulkResult.failures.map((failure) => (
                    <Typography variant="body2" key={`${failure.rowNumber}-${failure.publicRiderId}`}>
                      Row {failure.rowNumber} ({failure.publicRiderId || 'no rider ID'}): {failure.message}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)} disabled={bulkLoading}>Close</Button>
          <Button variant="contained" onClick={handleBulkSubmit} disabled={bulkLoading || bulkRows.length === 0} startIcon={bulkLoading ? <CircularProgress size={18} /> : <UploadFileIcon />}>
            {bulkLoading ? 'Uploading...' : 'Upload and update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={transactionDialogOpen}
        onClose={() => !transactionLoading && setTransactionDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>All Deficite Trasaction</DialogTitle>
        <DialogContent dividers>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Search by Public Rider ID"
              value={transactionSearchInput}
              onChange={(event) => setTransactionSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleTransactionSearch();
              }}
            />
            <Button variant="contained" startIcon={<SearchIcon />} onClick={handleTransactionSearch} disabled={transactionLoading}>
              Search
            </Button>
          </Stack>

          {transactionError && <Alert severity="error" sx={{ mb: 2 }}>{transactionError}</Alert>}

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
            {transactionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
              </Box>
            ) : transactions.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 5 }}>
                <Typography color="text.secondary">No deficit transactions found.</Typography>
              </Box>
            ) : (
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Transaction ID</TableCell>
                    <TableCell>Rider ID</TableCell>
                    <TableCell>Public Rider ID</TableCell>
                    <TableCell>Rider Name</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell align="right">Rent Amount</TableCell>
                    <TableCell align="right">Before Rent</TableCell>
                    <TableCell align="right">After Rent</TableCell>
                    <TableCell align="right">Damage Amount</TableCell>
                    <TableCell align="right">Before Damage</TableCell>
                    <TableCell align="right">After Damage</TableCell>
                    <TableCell align="right">Challen Amount</TableCell>
                    <TableCell align="right">Before Challen</TableCell>
                    <TableCell align="right">After Challen</TableCell>
                    <TableCell>Updated By</TableCell>
                    <TableCell>Updated On</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id} hover>
                      <TableCell>{transaction.id}</TableCell>
                      <TableCell>{transaction.riderId}</TableCell>
                      <TableCell>{transaction.publicRiderId || 'N/A'}</TableCell>
                      <TableCell>{transaction.rider.name}</TableCell>
                      <TableCell>{transaction.rider.phone}</TableCell>
                      <TableCell>{transaction.rider.city}</TableCell>
                      <TableCell align="right">{formatCurrency(transaction.rentAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(transaction.beforeRentAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(transaction.afterRentAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(transaction.damageAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(transaction.beforeDamageAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(transaction.afterDamageAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(transaction.channelAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(transaction.beforeChannelAmount)}</TableCell>
                      <TableCell align="right">{formatCurrency(transaction.afterChannelAmount)}</TableCell>
                      <TableCell>{transaction.updatedBy || 'N/A'}</TableCell>
                      <TableCell>{formatDate(transaction.updatedOn)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          {transactionTotal > 0 && (
            <TablePagination
              component="div"
              count={transactionTotal}
              page={transactionPage}
              rowsPerPage={20}
              rowsPerPageOptions={[20]}
              onPageChange={handleTransactionPageChange}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransactionDialogOpen(false)} disabled={transactionLoading}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={exportDialogOpen} onClose={() => !exporting && setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Download Deficit Details</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which deficit records to include. Date filters use the record's Updated At date.
          </Typography>
          <Stack spacing={2}>
            <TextField
              select
              fullWidth
              label="Download scope"
              value={exportMode}
              onChange={(event) => setExportMode(event.target.value as 'all' | 'city' | 'date')}
              SelectProps={{ native: false }}
            >
              <MenuItem value="all">All data</MenuItem>
              <MenuItem value="city">Based on City</MenuItem>
              <MenuItem value="date">Based on Updated Date</MenuItem>
            </TextField>

            {exportMode === 'city' && (
              <TextField
                select
                fullWidth
                required
                label="City"
                value={exportCity}
                onChange={(event) => setExportCity(event.target.value)}
                SelectProps={{ native: false }}
              >
                {cityOptions.map((city) => (
                  <MenuItem key={city} value={city}>{city}</MenuItem>
                ))}
              </TextField>
            )}

            {exportMode === 'date' && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="date" label="Updated from" value={exportFromDate} onChange={(event) => setExportFromDate(event.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth type="date" label="Updated to" value={exportToDate} onChange={(event) => setExportToDate(event.target.value)} InputLabelProps={{ shrink: true }} />
                </Grid>
                {exportFromDate && exportToDate && exportFromDate > exportToDate && (
                  <Grid item xs={12}>
                    <Alert severity="error">The start date cannot be after the end date.</Alert>
                  </Grid>
                )}
              </Grid>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} disabled={exporting}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={18} /> : <DownloadIcon />}
            onClick={exportDeficitDetails}
            disabled={exporting || !exportFilterValid || (exportMode === 'date' && Boolean(exportFromDate && exportToDate && exportFromDate > exportToDate))}
          >
            {exporting ? 'Exporting...' : 'Download Excel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Summary Cards */}
      {!loading && data.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Records
                </Typography>
                <Typography variant="h5">{total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Rent Pending
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(
                    data.reduce((sum, row) => sum + (row.totalRentAmount - row.totalRentPaid), 0)
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Damage Cost Pending</Typography>
                <Typography variant="h5">
                  {formatCurrency(
                    data.reduce((sum, row) => sum + (row.damageCost - row.totalDamagePaid), 0)
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Challen Amount Pending</Typography>
                <Typography variant="h5">
                  {formatCurrency(
                    data.reduce((sum, row) => sum + (row.channelAmount - row.totalChannelPaid), 0)
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Table Section */}
      <TableContainer component={Paper} sx={{ position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
              zIndex: 10,
              backdropFilter: 'blur(4px)',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {/* Empty State */}
        {!loading && data.length === 0 && (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              No deficit details found
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {searchPublicRiderId
                ? 'Try adjusting your search criteria'
                : 'No records available at this time'}
            </Typography>
          </Box>
        )}

        {/* Table */}
        {data.length > 0 && (
          <>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Public Rider ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Rider Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>City</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Rent Pending</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Damage Cost Pending</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Challen Amount Pending</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total Pending</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row) => {
                  const rentPending = row.totalRentAmount - row.totalRentPaid;
                  const channelPending = row.channelAmount - row.totalChannelPaid;
                  const damagePending = row.damageCost - row.totalDamagePaid;
                  const totalPending = calculatePending(row);

                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                        },
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={row.publicRiderId}
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>{row.rider.name}</TableCell>
                      <TableCell>{row.rider.phone}</TableCell>
                      <TableCell>
                        <Chip label={row.rider.city} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Box>
                          <Typography
                            sx={{
                              color: rentPending > 0 ? 'error.main' : 'success.main',
                              fontWeight: 600,
                            }}
                          >
                            {formatCurrency(rentPending)}
                          </Typography>
                          {rentPending > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {getRentWeekLabel(row.publicRiderId)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{
                            color: damagePending > 0 ? 'error.main' : 'success.main',
                            fontWeight: 600,
                          }}
                        >
                          {formatCurrency(damagePending)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{
                            color: channelPending > 0 ? 'error.main' : 'success.main',
                            fontWeight: 600,
                          }}
                        >
                          {formatCurrency(channelPending)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{
                            color: totalPending > 0 ? 'warning.main' : 'success.main',
                            fontWeight: 700,
                            fontSize: '1.05rem',
                          }}
                        >
                          {formatCurrency(totalPending)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(row.updatedAt)}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            />
          </>
        )}
      </TableContainer>
    </Box>
  );
};

export default DeficitDetails;
